import React, { useState, useCallback, useMemo } from "react";
import clsx from "clsx";
import type {
  LibraryFolder,
  LibraryFolders,
  LibraryItem,
  LibraryItems,
} from "../types";
import {
  LibraryMenuSection,
  LibraryMenuSectionGrid,
} from "./LibraryMenuSection";
import type { SvgCache } from "../hooks/useLibraryItemSvg";

import "./LibraryFolderView.scss";

const ChevronRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <path d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z" />
  </svg>
);

const FolderIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <path d="M1 3.5A1.5 1.5 0 0 1 2.5 2h3.879a1.5 1.5 0 0 1 1.06.44l1.122 1.12A.5.5 0 0 0 9.207 4H13.5A1.5 1.5 0 0 1 15 5.5v7a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 1 12.5v-9z" />
  </svg>
);

interface LibraryFolderViewProps {
  folders: LibraryFolders;
  items: LibraryItems;
  onItemSelectToggle: (id: LibraryItem["id"], event: React.MouseEvent) => void;
  onItemDrag: (id: LibraryItem["id"], event: React.DragEvent) => void;
  onItemClick: (id: LibraryItem["id"] | null) => void;
  isItemSelected: (id: LibraryItem["id"] | null) => boolean;
  svgCache: SvgCache;
  itemsRenderedPerBatch: number;
  onCreateFolder: (name: string, parentId: string | null) => void;
  onRenameFolder: (folderId: string, newName: string) => void;
  onDeleteFolder: (folderId: string, deleteItems: boolean) => void;
  onMoveItem: (itemId: string, folderId: string | null) => void;
  selectedItems: LibraryItem["id"][];
}

interface FolderTreeNode {
  folder: LibraryFolder | null; // null for root
  children: FolderTreeNode[];
  items: LibraryItems;
}

export const LibraryFolderView: React.FC<LibraryFolderViewProps> = ({
  folders,
  items,
  onItemSelectToggle,
  onItemDrag,
  onItemClick,
  isItemSelected,
  svgCache,
  itemsRenderedPerBatch,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder,
  onMoveItem,
  selectedItems,
}) => {
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
    new Set(["root"]),
  );
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null);
  const [editingFolderName, setEditingFolderName] = useState("");
  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null);

  // Build folder tree
  const folderTree = useMemo(() => {
    const buildTree = (parentId: string | null): FolderTreeNode => {
      const childFolders = folders.filter((f) => f.parentId === parentId);
      const folderItems = items.filter(
        (item) => (item.folderId || null) === parentId,
      );

      const node: FolderTreeNode = {
        folder:
          parentId === null
            ? null
            : folders.find((f) => f.id === parentId) || null,
        children: childFolders.map((f) => buildTree(f.id)),
        items: folderItems as LibraryItems,
      };

      return node;
    };

    return buildTree(null);
  }, [folders, items]);

  const toggleFolder = useCallback((folderId: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  }, []);

  const startRenameFolder = useCallback((folder: LibraryFolder) => {
    setEditingFolderId(folder.id);
    setEditingFolderName(folder.name);
  }, []);

  const finishRenameFolder = useCallback(() => {
    if (editingFolderId && editingFolderName.trim()) {
      onRenameFolder(editingFolderId, editingFolderName.trim());
    }
    setEditingFolderId(null);
    setEditingFolderName("");
  }, [editingFolderId, editingFolderName, onRenameFolder]);

  const handleItemDragStart = useCallback(
    (itemId: LibraryItem["id"], event: React.DragEvent) => {
      onItemDrag(itemId, event);
      event.dataTransfer.setData("library-item-id", itemId);
      event.dataTransfer.effectAllowed = "move";
    },
    [onItemDrag],
  );

  const handleFolderDragOver = useCallback(
    (folderId: string | null, event: React.DragEvent) => {
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      setDragOverFolderId(folderId);
    },
    [],
  );

  const handleFolderDrop = useCallback(
    (folderId: string | null, event: React.DragEvent) => {
      event.preventDefault();
      const itemId = event.dataTransfer.getData("library-item-id");
      if (itemId) {
        onMoveItem(itemId, folderId);
      }
      setDragOverFolderId(null);
    },
    [onMoveItem],
  );

  const handleFolderDragLeave = useCallback(() => {
    setDragOverFolderId(null);
  }, []);

  const renderFolder = useCallback(
    (node: FolderTreeNode, depth: number = 0): React.ReactNode => {
      const folderId = node.folder?.id || "root";
      const isExpanded = expandedFolders.has(folderId);
      const isEditing = editingFolderId === node.folder?.id;
      const isDragOver = dragOverFolderId === folderId;

      return (
        <div key={folderId} className="library-folder-view__folder">
          {node.folder && (
            <div
              className={clsx("library-folder-view__folder-header", {
                "library-folder-view__folder-header--drag-over": isDragOver,
              })}
              style={{ paddingLeft: `${depth * 20}px` }}
              onDragOver={(e) => handleFolderDragOver(node.folder!.id, e)}
              onDrop={(e) => handleFolderDrop(node.folder!.id, e)}
              onDragLeave={handleFolderDragLeave}
            >
              <button
                className="library-folder-view__folder-toggle"
                onClick={() => toggleFolder(folderId)}
                aria-label={isExpanded ? "Collapse folder" : "Expand folder"}
              >
                <span
                  className={clsx("library-folder-view__chevron", {
                    "library-folder-view__chevron--expanded": isExpanded,
                  })}
                >
                  <ChevronRightIcon />
                </span>
              </button>
              <FolderIcon />
              {isEditing ? (
                <input
                  className="library-folder-view__folder-name-input"
                  value={editingFolderName}
                  onChange={(e) => setEditingFolderName(e.target.value)}
                  onBlur={finishRenameFolder}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      finishRenameFolder();
                    } else if (e.key === "Escape") {
                      setEditingFolderId(null);
                      setEditingFolderName("");
                    }
                  }}
                  autoFocus
                />
              ) : (
                <span
                  className="library-folder-view__folder-name"
                  onDoubleClick={() => startRenameFolder(node.folder!)}
                >
                  {node.folder.name}
                </span>
              )}
              <div className="library-folder-view__folder-actions">
                <button
                  className="library-folder-view__folder-action"
                  onClick={() => startRenameFolder(node.folder!)}
                  title="Rename folder"
                >
                  ✏️
                </button>
                <button
                  className="library-folder-view__folder-action"
                  onClick={() => {
                    if (
                      window.confirm(
                        "Delete folder and move items to root? Click Cancel to delete items too.",
                      )
                    ) {
                      onDeleteFolder(node.folder!.id, false);
                    } else {
                      onDeleteFolder(node.folder!.id, true);
                    }
                  }}
                  title="Delete folder"
                >
                  🗑️
                </button>
                <button
                  className="library-folder-view__folder-action"
                  onClick={() => {
                    const name = prompt("Enter folder name:");
                    if (name?.trim()) {
                      onCreateFolder(name.trim(), node.folder!.id);
                      setExpandedFolders(
                        (prev) => new Set([...prev, folderId]),
                      );
                    }
                  }}
                  title="Create subfolder"
                >
                  ➕
                </button>
              </div>
            </div>
          )}

          {isExpanded && (
            <>
              {node.items.length > 0 && (
                <div
                  className="library-folder-view__items"
                  style={{ paddingLeft: `${(depth + 1) * 20}px` }}
                  onDragOver={(e) =>
                    handleFolderDragOver(node.folder?.id || null, e)
                  }
                  onDrop={(e) => handleFolderDrop(node.folder?.id || null, e)}
                  onDragLeave={handleFolderDragLeave}
                >
                  <LibraryMenuSectionGrid>
                    <LibraryMenuSection
                      itemsRenderedPerBatch={itemsRenderedPerBatch}
                      items={node.items}
                      onItemSelectToggle={onItemSelectToggle}
                      onItemDrag={handleItemDragStart}
                      onClick={onItemClick}
                      isItemSelected={isItemSelected}
                      svgCache={svgCache}
                    />
                  </LibraryMenuSectionGrid>
                </div>
              )}
              {node.children.map((child) => renderFolder(child, depth + 1))}
            </>
          )}
        </div>
      );
    },
    [
      expandedFolders,
      editingFolderId,
      editingFolderName,
      dragOverFolderId,
      toggleFolder,
      startRenameFolder,
      finishRenameFolder,
      handleItemDragStart,
      handleFolderDragOver,
      handleFolderDrop,
      handleFolderDragLeave,
      onCreateFolder,
      onDeleteFolder,
      onItemSelectToggle,
      onItemClick,
      isItemSelected,
      svgCache,
      itemsRenderedPerBatch,
    ],
  );

  return (
    <div className="library-folder-view">
      <div className="library-folder-view__toolbar">
        <button
          className="library-folder-view__create-folder-btn"
          onClick={() => {
            const name = prompt("Enter folder name:");
            if (name?.trim()) {
              onCreateFolder(name.trim(), null);
            }
          }}
        >
          ➕ New Folder
        </button>
      </div>
      {renderFolder(folderTree)}
    </div>
  );
};
